import FilterSelect from '@Components/Filter/Dropdown';
import Image from '@Components/Image';
import { RootStateType, AppDispatch } from '@Store';
import { alpha, Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLoggedInUserSystemList } from '../../store/reducers/Systems';
import API from '@Services/apiAxios';
import { AnalyticsData } from './types/type';
import { formatNumber, useLocale } from '../../utils/formatNumber';
import { convertWeightKGtoLB } from '../../utils/convertWeight';

const TopPerformanceView = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [system, setSystem] = useState<string[]>([]);
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const locale = useLocale();
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  // const [loading, setLoading] = useState<boolean>(true);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };

  useEffect(() => {
    dispatch(getLoggedInUserSystemList());
  }, []);

  const fetchAnalyticsStats = async () => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await API.get('/analytics/stats/today', {
        params: {
          date: currentDate,
          'system[]': system,
        },
      });
      setAnalyticsData(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics stats:', error);
    }
  };

  // console.info('analyticsData:', analyticsData);

  useEffect(() => {
    fetchAnalyticsStats();
  }, [system]);

  return (
    <Box
      sx={{
        borderRadius: { xs: '10px', sm: '12px' },
        border: '1px solid #F1F1F1',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.15)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: {
          xs: 2,
          sm: 2.5,
          md: 3,
        },
        padding: {
          xs: '14px',
          sm: '18px',
          md: '20px 24px',
          lg: '24px 28px',
        },
        overflow: 'hidden',
      }}
    >
      {/* Filter */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: {
            xs: 'stretch',
            sm: 'flex-end',
          },
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: {
              xs: '100%',
              sm: '220px',
              md: '240px',
            },
          }}
        >
          <FilterSelect
            id="system"
            label="Select system"
            options={loggedInUserSystemDropdownList}
            value={system}
            onChange={onSystemFilter}
          />
        </Box>
      </Box>

      {/* Main Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            xl: 'row',
          },
          alignItems: {
            xs: 'flex-start',
            xl: 'center',
          },
          gap: {
            xs: 2.5,
            md: 3,
            lg: 4,
          },
          width: '100%',
        }}
      >
        {/* Heading */}
        <Box
          sx={{
            width: {
              xs: '100%',
              xl: '180px',
            },
            minWidth: {
              xl: '180px',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: {
                xs: '18px',
                sm: '20px',
                md: '22px',
              },
              lineHeight: 1.3,
              position: 'relative',

              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: 0,
                width: '60px',
                height: '3px',
                backgroundColor: 'primary.main',
                borderRadius: '20px',
                display: {
                  xs: 'block',
                  xl: 'none',
                },
              },
            }}
          >
            Today&apos;s Performance
          </Typography>
        </Box>

        {/* Cards */}
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: {
              xs: 1.5,
              sm: 2,
            },
          }}
        >
          {/* Common Card Style */}
          {[
            {
              icon: '/images/icons/AdminHome/shipment.png',
              alt: 'shipment',
              percentage: analyticsData?.revenuePercentage,
              value: analyticsData?.totalShipment && formatNumber(analyticsData?.totalShipment, locale),
              label: 'Shipment',
            },
            {
              icon: '/images/icons/AdminHome/weight.png',
              alt: 'weight',
              percentage: analyticsData?.weightPercentage,
              value:
                name === 'Cargo Solutions Network'
                  ? analyticsData?.totalWeight && formatNumber(convertWeightKGtoLB(analyticsData?.totalWeight), locale)
                  : analyticsData?.totalWeight && formatNumber(analyticsData?.totalWeight, locale),
              label: `Total Weight ${name === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}`,
            },
            {
              icon: '/images/icons/AdminHome/revenue.png',
              alt: 'revenue',
              percentage: analyticsData?.shipmentPercentage,
              value: analyticsData?.totalRevenue && formatNumber(analyticsData?.totalRevenue, locale),
              label: `Total Revenue (${defaultOrgCountryCurrency})`,
            },
          ].map((item, index) => (
            <Box
              key={index}
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: {
                  xs: 1.25,
                  sm: 1.5,
                },
                padding: {
                  xs: '12px',
                  sm: '14px',
                  md: '16px',
                },
                backgroundColor: theme.palette.background.paper,
                borderRadius: {
                  xs: '10px',
                  md: '12px',
                },
                border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                transition: '0.2s ease',
                minWidth: 0,
                height: '100%',

                '&:hover': {
                  boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                  transform: 'translateY(-1px)',
                },
              })}
            >
              {/* Icon */}
              <Box
                sx={theme => ({
                  width: {
                    xs: '44px',
                    sm: '48px',
                    md: '52px',
                  },
                  height: {
                    xs: '44px',
                    sm: '48px',
                    md: '52px',
                  },
                  borderRadius: '10px',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                })}
              >
                <Image
                  src={item.icon}
                  alt={item.alt}
                  style={{
                    width: '60%',
                    height: '60%',
                    objectFit: 'contain',
                  }}
                />
              </Box>

              {/* Content */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* Percentage */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    py: '2px',
                    borderRadius: '5px',
                    backgroundColor: item.percentage >= 0 ? 'success.light' : 'error.light',
                    mb: 1,
                  }}
                >
                  <Image
                    src={
                      item.percentage >= 0
                        ? '/images/icons/AdminHome/upArrow.svg'
                        : '/images/icons/AdminHome/downArrow.svg'
                    }
                    alt="trend"
                    style={{
                      width: '8px',
                      height: '10px',
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: item.percentage >= 0 ? 'success.main' : 'error.main',
                      fontSize: {
                        xs: '10px',
                        sm: '11px',
                      },
                      lineHeight: 1,
                    }}
                  >
                    {formatNumber(item.percentage, locale)}%
                  </Typography>
                </Box>

                {/* Value */}
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: {
                      xs: '18px',
                      sm: '20px',
                      md: '24px',
                    },
                    lineHeight: 1.2,
                    mb: 0.3,
                    color: 'text.primary',
                    wordBreak: 'break-word',
                  }}
                >
                  {item.value}
                </Typography>

                {/* Label */}
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: {
                      xs: '11px',
                      sm: '12px',
                      md: '13px',
                    },
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TopPerformanceView;
